import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { API_BASE_URL } from '../tokens/api-base-url.token';
import { FEEDBACK_API_TOKEN_REFRESH_ENABLED } from '../tokens/token-refresh.token';
import { FeedbackApiTokenStore } from './feedback-api-token-store';
import { FeedbackApiTokenRefresher, feedbackApiTokenUrl } from './feedback-api-token-refresher';

/**
 * Attaches `Authorization: Bearer <token>` to requests bound for the Feedback
 * API. Scoped by {@link API_BASE_URL} so it never leaks the token to other
 * hosts, and a no-op when no token is stored.
 *
 * When {@link FEEDBACK_API_TOKEN_REFRESH_ENABLED} is on, a `401` is also
 * recovered from once: the interceptor fetches a replacement token and replays
 * the request. Without that, an expired stored token leaves every call failing
 * until the user signs out and back in.
 *
 * Register it in the app:
 * @example
 * provideHttpClient(withInterceptors([feedbackApiAuthInterceptor]))
 */
export const feedbackApiAuthInterceptor: HttpInterceptorFn = (request, next) => {
  const baseUrl = inject(API_BASE_URL);

  // Only attach the token to Feedback API calls.
  if (!baseUrl || !request.url.startsWith(baseUrl)) {
    return next(request);
  }

  // Respect a caller-supplied Authorization header (e.g. the auth bridge passing
  // a parent's validateround JWT) — never overwrite it with the stored token,
  // and never trade it for a wildcard one on failure.
  if (request.headers.has('Authorization')) {
    return next(request);
  }

  // `inject` is only legal in the interceptor body, so resolve the refresh
  // collaborators up front even though they are used inside the error handler.
  const storedToken = inject(FeedbackApiTokenStore).get();
  const refresher = inject(FeedbackApiTokenRefresher);
  const refreshEnabled = inject(FEEDBACK_API_TOKEN_REFRESH_ENABLED);

  const authorizedRequest = storedToken ? withBearerToken(request, storedToken) : request;

  // The token endpoint is excluded: refreshing in response to its own failure
  // would recurse.
  if (!refreshEnabled || request.url === feedbackApiTokenUrl(baseUrl)) {
    return next(authorizedRequest);
  }

  return next(authorizedRequest).pipe(
    catchError((requestFailure: unknown) => {
      if (!(requestFailure instanceof HttpErrorResponse) || requestFailure.status !== 401) {
        return throwError(() => requestFailure);
      }

      return refresher.refresh().pipe(
        switchMap((freshToken) =>
          // The replay is not itself wrapped, so a second 401 surfaces to the
          // caller rather than looping.
          freshToken
            ? next(withBearerToken(request, freshToken))
            : throwError(() => requestFailure),
        ),
      );
    }),
  );
};

function withBearerToken(request: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return request.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}
